import qs from 'qs'
import { storage } from '@/utils/storage'
import { STRAPI_BASE_URL } from '@/config/api'

const STRAPI_URL = STRAPI_BASE_URL
const REQUEST_TIMEOUT_MS = 15_000

function requestSignal(): AbortSignal {
  return AbortSignal.timeout(REQUEST_TIMEOUT_MS)
}

export class StrapiError extends Error {
  status: number
  details: unknown

  constructor(message: string, status: number, details?: unknown) {
    super(message)
    this.name = 'StrapiError'
    this.status = status
    this.details = details
  }
}

interface StrapiMeta {
  pagination?: {
    page: number
    pageSize: number
    pageCount: number
    total: number
  }
}

interface StrapiResponse<T> {
  data: T
  meta: StrapiMeta
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  const token = storage.getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    const message = body?.error?.message ?? response.statusText ?? 'Request failed'
    throw new StrapiError(message, response.status, body?.error?.details)
  }
  if (response.status === 204) return undefined as T
  return response.json()
}

export async function strapiGet<T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<StrapiResponse<T>> {
  const query = params ? `?${qs.stringify(params, { encodeValuesOnly: true })}` : ''
  const res = await fetch(`${STRAPI_URL}${path}${query}`, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: requestSignal(),
  })
  return handleResponse<StrapiResponse<T>>(res)
}

export async function strapiGetAll<T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<T[]> {
  const requestedPagination = (params?.pagination ?? {}) as Record<string, unknown>
  const requestedPageSize = Number(requestedPagination.pageSize ?? 100)
  const pageSize = Math.min(Math.max(Number.isFinite(requestedPageSize) ? requestedPageSize : 100, 1), 100)
  const firstPage = await strapiGet<T[]>(path, {
    ...params,
    pagination: {
      ...requestedPagination,
      page: 1,
      pageSize,
    },
  })
  const firstRows = Array.isArray(firstPage.data) ? firstPage.data : []
  const pageCount = firstPage.meta?.pagination?.pageCount ?? 1
  if (pageCount <= 1) return firstRows

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      strapiGet<T[]>(path, {
        ...params,
        pagination: {
          ...requestedPagination,
          page: index + 2,
          pageSize,
        },
      }),
    ),
  )

  return remainingPages.reduce<T[]>(
    (items, page) => items.concat(Array.isArray(page.data) ? page.data : []),
    firstRows,
  )
}

export async function strapiPost<T>(
  path: string,
  body: unknown,
): Promise<StrapiResponse<T>> {
  const res = await fetch(`${STRAPI_URL}${path}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
    signal: requestSignal(),
  })
  return handleResponse<StrapiResponse<T>>(res)
}

export async function strapiPatch<T>(
  path: string,
  body: unknown,
): Promise<StrapiResponse<T>> {
  const res = await fetch(`${STRAPI_URL}${path}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
    signal: requestSignal(),
  })
  return handleResponse<StrapiResponse<T>>(res)
}

export async function strapiPut<T>(
  path: string,
  body: unknown,
): Promise<StrapiResponse<T>> {
  const res = await fetch(`${STRAPI_URL}${path}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
    signal: requestSignal(),
  })
  return handleResponse<StrapiResponse<T>>(res)
}

export async function strapiDelete(path: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}${path}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    signal: requestSignal(),
  })
  await handleResponse<void>(res)
}
